import React, { useContext, useEffect, useState } from 'react';
import { styled, useTheme, Theme, useMediaQuery } from '@mui/material';
import { graphql, useStaticQuery, Link } from 'gatsby';
import '../assets/fonts/loader.css';
import { GlobalHeader, HeaderLink } from './header';
import { GlobalHeaderProps } from './header/GlobalHeader';
import { HideOnScroll } from './header/utils';
import Logo from '../assets/images/logo.svg';
import { useGlobalState } from './GlobalState';
import {
  HPID_LOGIN_URL,
  HPID_LOGOUT_URL,
  LAPTOP_NAME,
  LAPTOP_SKU,
} from '../settings';
import Button from './button';
import { Icon } from './icon';
import GlobalFooter from './footer/GlobalFooter';
import SupportModal from './supportinfo-modal';
import { useProductIdsQuery } from '../hooks/useOrders';
import { useGetProduct, useGetCheckout } from '../hooks/useShopify';
import { Order } from './profile/OrderHistory/types';
import { CartContext } from '../context/CartContext';
import { SupportContext } from '../context/SupportContext';

export interface LayoutProps {
  children?: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  noScrollHeader?: boolean;
  hideHeaderLinks?: boolean;
  hideFooterLinks?: boolean;
  mainStyles?: React.CSSProperties;
}

const Page = styled('div')`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.palette.greyscale.white};
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.palette.greyscale.black} 0%,
    ${({ theme }) => theme.palette.navy} 100%
  );
  background-position: center top;
  background-repeat: no-repeat;
  background-size: 100% auto;
  min-height: 100vh;
  a {
    color: ${({ theme }) => theme.palette.greyscale.white};
    text-decoration: none;
  }
  p {
    font-family: 'HCo Gotham SSm';
  }
`;

const CustomHeader = styled(GlobalHeader)`
  border-bottom: 1px solid
    ${({ theme }: { theme: Theme }) => theme.palette.greyscale['06']};
`;

const CustomHeaderLink = styled(HeaderLink)`
  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    font-size: 10px;
    letter-spacing: 1.2px;
    padding: 6px;

    .MuiButtonBase-root {
      &.MuiButton-root {
        .MuiButton-startIcon {
          margin: 0px;
        }
      }
    }
  }
`;

const CustomButton = styled(Button)`
  padding: 6px 12px 6px 26px;
  min-width: max-content;

  &.MuiButton-root {
    text-decoration: none;
    letter-spacing: 2px;
    background: none;
    &:hover,
    &:active,
    &:focus,
    &:visited,
    &:focus-visible {
      background: none;
      color: inherit;
    }

    svg {
      font-size: 24px;
      color: ${({ theme }) => theme.palette.greyscale.white};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    font-size: 10px;
    letter-spacing: 1.2px;
    padding: 6px 0px 6px 12px;
    width: 36px;
    height: 48px;

    &.MuiButton-root {
      .MuiButton-startIcon {
        margin: 0px;
      }
    }
  }
`;
const MenuIcon = styled('button')`
  position: fixed;
  top: 1.6rem;
  right: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 1.5rem;
  height: 1.2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 5;
  div {
    width: 1.5rem;
    height: 0.2rem;
    background: white;
    border-radius: 12px;
    transform-origin: 2px;
    position: relative;
    transition: opacity 300ms, transform 300ms;
  }
`;

const First = styled('div')`
  transform: rotate(45deg);
`;

const Third = styled('div')`
  transform: rotate(-45deg);
`;

const MenuLinks = styled('nav')`
  display: ${({ nav }) => (nav ? 'flex' : 'none')};
  flex-direction: column;
  margin-top: 30px;
  text-align: left;
  height: calc(100vh - 50px);
  width: 100%;
  background: black;
  color: white;
  position: absolute;
  top: 50px;
  right: 0;
  transition: transform 300ms;
  overflow: hidden;
`;

const Hr = styled('div')`
  width: auto;
  margin: 5px 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.3);
  height: 0.2px;
`;
const GlobalLinks: React.FC = () => {
  const [{ user, purchased }, dispatch] = useGlobalState();
  const [nav, showNav] = useState(false);
  const { setShowCart } = useContext(CartContext);
  const theme = useTheme();

  const { open, setOpen } = useContext(SupportContext);
  const handleClickOpen = () => {
    showNav(false);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    showNav(true);
  };

  useEffect(() => {
    if (window.location.pathname === '/cart') {
      setShowCart(false);
    }
  }, []);
  const hasPurchasedProducts = purchased.length > 0;
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );
  return (
    <>
      {isMobile ? (
        <div>
          <MenuIcon nav={nav} onClick={() => showNav(!nav)}>
            {nav ? (
              <>
                <First />
                <Third />
              </>
            ) : (
              <>
                <div />
                <div />
                <div />
              </>
            )}
          </MenuIcon>
          <MenuLinks nav={nav}>
            <>
              <CustomHeaderLink title="Faq" to="/faq" className="userButton" />
              <Hr />
              {user && (
                <CustomHeaderLink
                  title="Account"
                  to="/user/account"
                  className="userButton"
                  onClick={() => showNav(false)}
                />
              )}
              {user && <Hr />}
              {hasPurchasedProducts && (
                <CustomHeaderLink
                  title="Support"
                  to="/user/support"
                  className="userButton"
                />
              )}
              {hasPurchasedProducts && <Hr />}
              {!hasPurchasedProducts && (
                <CustomButton
                  style={{
                    width: isMobile ? '' : 'auto',
                    fontSize: '14px',
                    textAlign: 'left',
                  }}
                  variant="ghost"
                  color="white"
                  text="Support"
                  onClick={handleClickOpen}
                  className="userButton"
                />
              )}
              {!hasPurchasedProducts && <Hr />}
              {user && (
                <CustomButton
                  style={{ fontSize: '14px' }}
                  variant="ghost"
                  color="white"
                  text="Sign out"
                  href={HPID_LOGOUT_URL}
                  onClick={() => {
                    showNav(false);
                    dispatch({ type: 'USER_LOGOUT' });
                  }}
                  className="userButton"
                />
              )}
              {user && <Hr />}
              {!user && (
                <CustomButton
                  style={{ width: isMobile ? '' : 'auto', fontSize: '14px' }}
                  variant="ghost"
                  color="white"
                  text="Sign in"
                  href={HPID_LOGIN_URL}
                  className="userButton"
                  onClick={() => showNav(false)}
                />
              )}
              {!user && <Hr />}
              <SupportModal open={open} onClose={handleClose} />
            </>
          </MenuLinks>
        </div>
      ) : (
        <>
          <CustomHeaderLink title="Faq" to="/faq" className="userButton" />
          {user && (
            <CustomHeaderLink
              title={!isMobile ? 'Account' : 'Account'}
              to="/user/account"
              startIcon={<Icon iconName="account" />}
              className="userButton"
            />
          )}
          {hasPurchasedProducts && (
            <CustomHeaderLink
              title="Support"
              to="/user/support"
              className="userButton"
            />
          )}
          {!hasPurchasedProducts && (
            <CustomButton
              style={{ width: isMobile ? '' : 'auto', fontSize: '14px' }}
              variant="ghost"
              color="white"
              text="Support"
              onClick={handleClickOpen}
              className="userButton"
            />
          )}
          {user && (
            <CustomButton
              variant="ghost"
              color="white"
              text={!isMobile ? 'Sign out' : ''}
              href={HPID_LOGOUT_URL}
              onClick={() => {
                dispatch({ type: 'USER_LOGOUT' });
              }}
              startIcon={isMobile ? <Icon iconName="exit" /> : null}
              className="userButton"
            />
          )}
          {!user && (
            <CustomButton
              style={{ width: isMobile ? '' : 'auto', fontSize: '14px' }}
              variant="ghost"
              color="white"
              text="Sign in"
              href={HPID_LOGIN_URL}
              className="userButton"
            />
          )}
          <SupportModal open={open} onClose={handleClose} />
        </>
      )}
    </>
  );
};

const SiteHeader: React.FC<{
  position: GlobalHeaderProps['position'];
  hideHeaderLinks: boolean;
}> = ({ position, hideHeaderLinks = false }) => (
  <CustomHeader
    position={position}
    left={
      <Link to="/" title="Home">
        <Logo />
      </Link>
    }
    right={hideHeaderLinks ? null : <GlobalLinks />}
  />
);

const StickyHeader = styled(SiteHeader)`
  position: fixed;
  top: 0;
  left: 0;
`;

const Layout: React.FC<LayoutProps> = ({
  children,
  hideHeader,
  hideFooter = false,
  noScrollHeader = false,
  hideHeaderLinks = false,
  hideFooterLinks = false,
  mainStyles = {},
}) => {
  const [{ user, checkoutId }, dispatch] = useGlobalState();
  const [getCheckout] = useGetCheckout();

  const [getProduct] = useGetProduct<{
    product: {
      variants: {
        edges: {
          node: {
            id: string;
            price: string;
            quantityAvailable: number;
          };
        }[];
      };
    };
  }>();
  const [getPurchasedData] = useProductIdsQuery(10);

  // Get static product info from gatsby
  const { staticProducts } = useStaticQuery<GatsbyTypes.StaticProductsQuery>(
    graphql`
      query StaticProducts {
        staticProducts: allShopifyProduct {
          nodes {
            handle
            storefrontId
            variants {
              shopifyId
              sku
              storefrontId
              product {
                shopifyId
                storefrontId
                title
                images {
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    `,
  );

  useEffect(() => {
    const transformedProductsPromises = staticProducts?.nodes?.reduce(
      (output: Promise<any>[], product) => {
        const staticVariant = product.variants?.[0];
        // Get dynamic product info from shopify
        return [
          ...output,
          getProduct({
            variables: {
              id: product.storefrontId,
            },
            // eslint-disable-next-line consistent-return
          }).then(({ data }) => {
            const dynamicVariant = data?.product?.variants?.edges?.[0]?.node;
            if (
              dynamicVariant &&
              staticVariant &&
              staticVariant.sku &&
              staticVariant.shopifyId &&
              staticVariant.storefrontId?.length &&
              staticVariant.product.title?.length
            ) {
              return {
                id: staticVariant.shopifyId ?? staticVariant.product.shopifyId,
                storefrontId:
                  staticVariant.storefrontId ??
                  staticVariant.product.storefrontId,
                cartName: staticVariant?.product.title,
                name:
                  staticVariant.sku === LAPTOP_SKU
                    ? LAPTOP_NAME
                    : staticVariant?.product.title,
                image: staticVariant.product.images?.[0]?.gatsbyImageData,
                handle: product.handle,
                price: parseFloat(dynamicVariant.price),
                sku: staticVariant.sku,
                stock: dynamicVariant.quantityAvailable,
              };
            }
          }),
        ];
      },
      [],
    );

    Promise.all(transformedProductsPromises ?? []).then((products) => {
      dispatch({
        type: 'PRODUCTS_ADD',
        products: products.filter((product) => product !== undefined),
      });
    });
  }, [staticProducts]);

  // Get purchased products into global state
  useEffect(() => {
    if (user) {
      const productIds: string[] = [];
      getPurchasedData().then(({ data }: any) => {
        data?.orders?.forEach(({ shopifyOrder }: Order) => {
          shopifyOrder?.lineItems.edges.forEach((lineItem) => {
            if (lineItem.node?.variant?.id) {
              productIds.push(lineItem.node.variant.id);
            }
          });
        });
        dispatch({ type: 'PURCHASED_ADD', productIds });
      });
    }
  }, [user]);

  /**
   * Handle clearing cart if checkout is completed
   */
  useEffect(() => {
    if (checkoutId) {
      getCheckout({
        variables: {
          id: checkoutId,
        },
      }).then(({ data }) => {
        if (data?.node.completedAt) {
          dispatch({ type: 'CART_RESET' });
        }
      });
    }
  }, []);

  /**
   * Trigger FreshWorks A/B Experiments
   */
  useEffect(() => {
    if (typeof window !== 'undefined' && window.FMApi) {
      window.FMApi.manualTriggerExperiment();
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (typeof window !== 'undefined' && window.FreshworksWidget) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.FreshworksWidget('close');
    }
  }, []);

  return (
    <Page sx={{ zIndex: 1 }}>
      {/* {myCart ? <CartModal /> : ' '} */}
      {!hideHeader && !noScrollHeader && (
        <HideOnScroll>
          <SiteHeader position="static" hideHeaderLinks={hideHeaderLinks} />
        </HideOnScroll>
      )}
      {!hideHeader && noScrollHeader && (
        <StickyHeader position="sticky" hideHeaderLinks={hideHeaderLinks} />
      )}

      <main style={mainStyles}>{children}</main>
      {!hideFooter && <GlobalFooter hideFooterLinks={hideFooterLinks} />}
    </Page>
  );
};

export default Layout;
